import React from "react";

interface ShopDpstResultLayerProps {
    loading: boolean;
    resultData: any;
    orderInfo: any;
    langData: any;
    pymtGuideCtxt: string;
    totMny: string;
    myCashTotMny?: number | string;
    fallbackPrdtNm?: string;
    onClose?: () => void;
    onRequestClose?: () => void;
    useShopLayout?: boolean;
    showGuide?: boolean;
}

const ShopDpstResultLayer = ({
    loading,
    resultData,
    orderInfo,
    langData,
    pymtGuideCtxt,
    totMny,
    myCashTotMny,
    fallbackPrdtNm,
    onClose,
    onRequestClose,
    useShopLayout = true,
    showGuide = false,
}: ShopDpstResultLayerProps) => {
    const renderInfoRow = (
        key: string,
        label: string,
        value: React.ReactNode,
        valueClassName: string = "value"
    ) => (
        <div key={key} className={itemClassName}>
            <span className="label">{label}</span>
            <span className={valueClassName}>{value}</span>
        </div>
    );

    const formatNumber = (value: any) => {
        const n = Number(value ?? 0);
        if (!Number.isFinite(n)) {
            return "0";
        }
        return n.toLocaleString();
    };

    const parseNumericValue = (value: any): number => {
        const parsed = Number(String(value ?? 0).replace(/,/g, ""));
        return Number.isFinite(parsed) ? parsed : 0;
    };

    const dpstAmtRaw =
        orderInfo?.odrAmt ??
        orderInfo?.dpstOdrAmt ??
        orderInfo?.payAmt ??
        orderInfo?.prdtPrc ??
        resultData?.odrAmt ??
        0;

    const paidMnyRaw = orderInfo?.paidMny ?? orderInfo?.dpstPaidMny ?? resultData?.paidMny ?? 0;
    const freeMnyRaw = orderInfo?.freeMny ?? orderInfo?.dpstFreeMny ?? resultData?.freeMny ?? 0;
    const gameMnyNm = orderInfo?.gameMnyNm || "";
    const dpstCode = String(resultData?.dpstCode || "");
    const pymtMthdNm = String(resultData?.pymtMthdNm || "");
    const pchsKindCd = String(resultData?.pchsKindCd || "DPST").toUpperCase();
    const isItemPurchase = pchsKindCd === "ITEM";
    const isSuccess = dpstCode === "PYMT0000" || dpstCode === "PYMT3502";
    const successTitle = isItemPurchase
        ? (langData?.GAME_ITEM_PCHS_RSLT_SUCC_MSG_00 || "아이템 구매가 완료되었습니다!")
        : (langData?.GAME_DPST_RSLT_SUCC_MSG_00 || "충전이 완료되었습니다!");
    const failureTitle = isItemPurchase
        ? (langData?.GAME_ITEM_PCHS_RSLT_FAIL_MSG_00 || "아이템 구매에 실패했습니다.")
        : (langData?.GAME_DPST_RSLT_FAIL_MSG_00 || "충전에 실패했습니다.");

    const cardClassName = useShopLayout ? "dpstResultWrap dpstResultCardLike" : "dpstResultWrap";
    const headerClassName = useShopLayout ? "dpstResultHeader dpstResultHeaderLike" : "dpstResultHeader";
    const iconClassName = `${isSuccess ? "successIcon" : "failureIcon"}${useShopLayout ? " resultIconLike" : ""}`;
    const titleClassName = useShopLayout ? "dpstResultTitleLike" : "";
    const infoClassName = useShopLayout ? "dpstResultInfo dpstResultInfoLike" : "dpstResultInfo";
    const itemClassName = useShopLayout ? "dpstResultItem dpstResultItemLike" : "dpstResultItem";
    const closeWrapClassName = useShopLayout ? "dpstResultBtnWrap dpstResultBtnWrapLike" : "dpstResultBtnWrap";
    const closeBtnClassName = useShopLayout ? "dpstResultBtn dpstResultBtnLike" : "dpstResultBtn secondary";
    const productName =
        orderInfo?.dpstPrdtNm ||
        orderInfo?.prdtNm ||
        resultData?.prdtNm ||
        fallbackPrdtNm ||
        "-";
    const rawAmountValue = orderInfo?.odrAmt ?? resultData?.odrAmt ?? dpstAmtRaw;
    const amountValue =
        typeof rawAmountValue === "number"
            ? rawAmountValue.toLocaleString()
            : String(rawAmountValue || "0");
    const paidLabel = langData?.GAME_DPST_RSLT_PAID_LBL_00 || "유료";
    const freeLabel = langData?.GAME_DPST_RSLT_FREE_LBL_00 || "무료";
    const rowItems: React.ReactNode[] = [
        renderInfoRow(
            "row-prdt-nm",
            langData?.GAME_DPST_RSLT_PRDT_NM_00 || "상품 명",
            productName
        ),
        renderInfoRow(
            "row-odr-amt",
            langData?.GAME_SHOP_TOT_PRC_LBL_00 || "결제 금액",
            amountValue,
            "value accent"
        ),
    ];

    if (isItemPurchase) {
        const remainingRaw = parseNumericValue(
            myCashTotMny ?? totMny ?? orderInfo?.totMny ?? 0
        );
        rowItems.push(
            renderInfoRow(
                "row-remain-mny",
                langData?.GAME_DPST_RSLT_CURR_MNY_00 || "잔여금액",
                `${formatNumber(remainingRaw)}${gameMnyNm}`,
                "value highlight"
            )
        );
    } else {
        rowItems.push(
            renderInfoRow(
                "row-pymt-mthd",
                langData?.GAME_MNY_DPST_MTHD_TITL_00 || "결제 수단",
                pymtMthdNm || "-"
            )
        );

        if (pchsKindCd === "DPST") {
            if (Number(paidMnyRaw) > 0 || Number(freeMnyRaw) > 0) {
                rowItems.push(
                    renderInfoRow(
                        "row-paid-free",
                        langData?.GAME_DPST_RSLT_CURR_MNY_00 || "충전된 캐시",
                        `${formatNumber(Number(paidMnyRaw) + Number(freeMnyRaw))}${gameMnyNm} (${paidLabel} ${formatNumber(paidMnyRaw)} + ${freeLabel} ${formatNumber(freeMnyRaw)})`
                    )
                );
            } else {
                rowItems.push(
                    renderInfoRow(
                        "row-paid-free-empty",
                        langData?.GAME_DPST_RSLT_CURR_MNY_00 || "충전된 캐시",
                        `0${gameMnyNm} (${paidLabel} 0 + ${freeLabel} 0)`
                    )
                );
            }
        }
    }


    const cardBody = (
        <div className={cardClassName}>
            {loading ? (
                <div className="loadingWrap">
                    <p>{langData?.GAME_DPST_RSLT_LOAD_MSG_00 || "충전 결과를 확인하고 있습니다..."}</p>
                </div>
            ) : (
                <>
                    <div className={headerClassName}>
                        <div className={iconClassName}>{isSuccess ? "✓" : "✗"}</div>
                        <h1 className={titleClassName}>{isSuccess ? successTitle : failureTitle}</h1>
                        {useShopLayout && <div className="dpstResultDividerLike" />}
                    </div>

                    <div className={infoClassName}>
                        {rowItems}
                    </div>

                    {showGuide && (
                        <div className={`dpstResultNotice${isSuccess ? "" : " failure"}`}>
                            <h3>{langData?.GAME_DPST_RSLT_GUID_00 || "안내사항"}</h3>
                            <div
                                dangerouslySetInnerHTML={{
                                    __html:
                                        pymtGuideCtxt ||
                                        (langData && langData.GAME_MNY_DPST_DESC_00) ||
                                        "결제 진행 시 유의사항을 꼭 확인하시기 바랍니다.",
                                }}
                            />
                        </div>
                    )}

                    {(onRequestClose || onClose) && (
                        <div className={closeWrapClassName}>
                            <button className={closeBtnClassName} onClick={onRequestClose || onClose}>
                                <span>{langData?.GAME_DPST_RSLT_CLOSE_BTN_00 || "닫기"}</span>
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );

    if (!useShopLayout) {
        return cardBody;
    }

    return (
        <div className="shopCtntStepWrap">
            <div className="shopContainer shopCtntStepBody">{cardBody}</div>
        </div>
    );
};

export default ShopDpstResultLayer;
